import { nanoid } from 'nanoid';
import {
  AttributeMap,
  FilterResults,
  MaterialType,
  SvgIds,
  ViewBox,
} from '@modules/Vector/S/Function/composite/svg.composite.types.ts';
import { SVG_COMPOSITE_CONSTANTS } from '@modules/Vector/S/Constant/composite.constant.ts';
import {
  appendChild,
  appendChildren,
  generateUniqueFilterResults,
  setAttributes,
  setNSAttributes,
} from '@modules/Vector/S/Function/composite/svg.utils.ts';

/**
 * @COMMENT - 스트로크 속성 복사 유틸리티 함수
 * SVG 요소에서 스트로크 관련 속성들을 복사하여 AttributeMap으로 반환
 *
 * 📝 상세 설명:
 * - 컷라인 요소의 시각적 속성들을 아웃라인 요소로 복사
 * - stroke, stroke-width, stroke-linecap 등 스트로크 관련 속성들만 추출
 * - null/undefined 값들은 필터링하여 깨끗한 AttributeMap 반환
 * - 아웃라인 효과 생성 시 필수적인 속성 복사 기능
 */
export const copyStrokeAttributes = (sourceElement: SVGPathElement): AttributeMap => {
  const strokeAttributes: AttributeMap = {};

  SVG_COMPOSITE_CONSTANTS.ATTRIBUTES.STROKE_PROPS.forEach((attr) => {
    const value = sourceElement.getAttribute(attr);
    if (value != null) {
      strokeAttributes[attr] = value;
    }
  });

  return strokeAttributes;
};

/**
 * @COMMENT - Overlay 타입용 필터 요소 생성
 * 원본 알파 채널만 유지하여 빈 영역을 투명하게 처리
 *
 * 📝 상세 설명:
 * - feComposite의 'in' 연산자를 사용하여 블렌드된 결과와 원본 알파 채널만 유지
 * - 재질이 적용된 영역 외부는 투명하게 처리되어 깔끔한 합성 효과
 * - 스티커 외곽선 내부에서만 재질이 보이도록 마스킹 효과
 */
export const createOverlayFilterElements = (doc: Document, filterResults: FilterResults): Element[] => {
  const feComp = doc.createElementNS(
    SVG_COMPOSITE_CONSTANTS.NAMESPACES.SVG,
    'feComposite',
  ) as SVGFECompositeElement;

  setAttributes(feComp, {
    in: filterResults.blended,
    in2: 'SourceAlpha',
    operator: 'in',
    result: filterResults.final,
  });

  return [feComp];
};

/**
 * @COMMENT - Multiply 타입용 필터 요소 생성
 * 배경 재질과 섞인 원본을 레이어로 병합
 *
 * 📝 상세 설명:
 * - feMerge를 사용하여 재질 이미지와 블렌드된 결과를 레이어로 쌓음
 * - 재질 이미지가 배경으로, 블렌드된 원본이 전면으로 배치
 * - 두 레이어가 자연스럽게 섞이는 multiply 효과 구현
 * - 더 풍부한 텍스처 표현을 위한 레이어 합성 방식
 */
export const createMultiplyFilterElements = (doc: Document, filterResults: FilterResults): Element[] => {
  const feMerge = doc.createElementNS(SVG_COMPOSITE_CONSTANTS.NAMESPACES.SVG, 'feMerge') as SVGFEMergeElement;

  // 첫 번째 노드: 재질 이미지 (배경 레이어)
  const n1 = doc.createElementNS(
    SVG_COMPOSITE_CONSTANTS.NAMESPACES.SVG,
    'feMergeNode',
  ) as SVGFEMergeNodeElement;
  setAttributes(n1, { in: filterResults.material });

  // 두 번째 노드: 블렌드된 결과 (전면 레이어)
  const n2 = doc.createElementNS(
    SVG_COMPOSITE_CONSTANTS.NAMESPACES.SVG,
    'feMergeNode',
  ) as SVGFEMergeNodeElement;
  setAttributes(n2, { in: filterResults.blended });

  // feMerge에 노드들 추가 (순서대로 렌더링)
  appendChildren(feMerge, [n1, n2]);
  setAttributes(feMerge, { result: filterResults.final });

  return [feMerge];
};

/**
 * @COMMENT - 재질 타입에 따른 필터 요소들을 생성하여 반환
 * 재질 타입별로 적절한 필터 요소들을 생성하는 디스패처 함수
 *
 * 📝 상세 설명:
 * - switch문을 활용하여 재질 타입별로 다른 필터 요소 생성
 * - overlay: 알파 채널 마스킹 방식
 * - multiply: 레이어 병합 방식
 * - 정의되지 않은 타입은 overlay로 fallback 처리
 * - 타입 안전성을 위한 switch-case 패턴 활용
 */
export const createMaterialTypeFilterElements = (
  doc: Document,
  materialType: MaterialType,
  filterResults: FilterResults,
): Element[] => {
  switch (materialType) {
    case SVG_COMPOSITE_CONSTANTS.MATERIAL_TYPES.OVERLAY:
      return createOverlayFilterElements(doc, filterResults);

    case SVG_COMPOSITE_CONSTANTS.MATERIAL_TYPES.MULTIPLY:
      return createMultiplyFilterElements(doc, filterResults);

    default:
      // 정의되지 않은 타입은 overlay로 처리 (안전한 fallback)
      console.warn(`알 수 없는 재질 타입 '${materialType}', overlay로 처리합니다`);
      return createOverlayFilterElements(doc, filterResults);
  }
};

/**
 * @COMMENT - 고유한 ID들을 안전하게 생성
 * SVG 요소들의 고유 식별자를 생성합니다.
 *
 * 📝 상세 설명:
 * - SVG 요소들의 고유 식별자를 생성 (cut, clip, filter 용도별)
 * - nanoid를 사용하여 충돌 가능성이 극히 낮은 ID 생성
 * - 기존 Date.now() 방식보다 더 안전하고 예측 불가능
 * - 3글자로 짧게 유지하여 가독성과 성능 최적화
 * - 동시에 여러 SVG가 처리될 때 ID 충돌 방지
 */
export const generateUniqueIds = (): SvgIds => {
  // nanoid 3글자로 충분한 고유성 보장
  const uid = nanoid(3);

  return {
    cut: `cut-${uid}`, // 컷라인 패스 ID
    clip: `clip-${uid}`, // 클립패스 ID
    filter: `matfx-${uid}`, // 필터 ID
  };
};

/**
 * @COMMENT - 재질 필터를 생성하는 헬퍼 함수
 * SVG 필터 체인을 구성하여 재질 이미지와 원본 디자인을 자연스럽게 합성합니다.
 *
 * 📝 상세 설명:
 * - feImage: 재질 이미지를 필터 입력으로 설정
 * - feBlend: 재질과 원본을 지정된 모드로 합성 (overlay/multiply)
 * - 타입별 후처리: overlay는 알파 채널 마스킹, multiply는 레이어 병합
 * - viewBox 크기에 맞춰 필터 영역을 정확하게 설정
 * - sRGB 색상 보간법으로 브라우저간 일관된 색상 표현 보장
 * - 고유 result 이름으로 다중 필터 실행 시 충돌 방지
 */
export const createMaterialFilter = (
  doc: Document,
  viewBox: ViewBox,
  dataUri: string,
  materialType: MaterialType,
  ids: SvgIds,
  defs: SVGDefsElement,
): void => {
  const filter = doc.createElementNS(SVG_COMPOSITE_CONSTANTS.NAMESPACES.SVG, 'filter') as SVGFilterElement;

  // 🎯 신뢰성 향상: 고유 result 이름 생성 (충돌 방지)
  const filterResults = generateUniqueFilterResults(ids.filter);

  // 필터 속성 설정 (추상화 적용)
  setAttributes(filter, {
    id: ids.filter,
    filterUnits: 'userSpaceOnUse',
    x: viewBox.x,
    y: viewBox.y,
    width: viewBox.width,
    height: viewBox.height,
    style: 'color-interpolation-filters:sRGB',
  });

  // 재질 입력 feImage 생성
  const feImg = doc.createElementNS(SVG_COMPOSITE_CONSTANTS.NAMESPACES.SVG, 'feImage') as SVGFEImageElement;
  setAttributes(feImg, {
    x: viewBox.x,
    y: viewBox.y,
    width: viewBox.width,
    height: viewBox.height,
    preserveAspectRatio: 'xMidYMid slice',
    result: filterResults.material,
  });
  setNSAttributes(feImg, {
    [SVG_COMPOSITE_CONSTANTS.NAMESPACES.XLINK]: {
      href: dataUri,
      'xlink:href': dataUri,
    },
  });

  // 재질 블렌드 feBlend 생성
  const feBlend = doc.createElementNS(SVG_COMPOSITE_CONSTANTS.NAMESPACES.SVG, 'feBlend') as SVGFEBlendElement;
  setAttributes(feBlend, {
    mode: materialType,
    in: 'SourceGraphic', // 필터 대상(원본 use)
    in2: filterResults.material,
    result: filterResults.blended,
  });

  // 재질 타입별 필터 요소들 생성 (분리된 함수 활용)
  const additionalElements = createMaterialTypeFilterElements(doc, materialType, filterResults);

  // 필터에 모든 요소들 추가 (추상화 적용)
  appendChildren(filter, [feImg, feBlend, ...additionalElements]);
  appendChild(defs, filter);
};
